<template>
  <div class="map" v-shortkey="navigateTableShortkeys" @shortkey="handleShortKey" v-resize="resizeMap">
    <v-app-bar app flat :height="mapTopHeight" color="#f7f9fc">
      <div class="filters px-0" ref="tableTop" v-if="configured">
        <div class="filters__inputs">
          <SearchBar :table-name="tableName" :config="coursesMap" />
          <StandingTime :table-name="tableName" :all-filters="readJSON(filters)" @changeFilters="changeFilters" />
          <DateRangePicker :table-name="tableName" />
          <ShowFiltersButton :table-name="tableName" />
        </div>
        <MapFilters
          :key="areResourcesBeingLoaded"
          :map-name="tableName"
          :filters="readJSON(allFilters)"
          :display-mode="displayMode"
          :show-display-mode="true"
          @changeDisplayMode="displayMode = $event"
        />
        <MapSwitch :view-name="tableName" />
      </div>
    </v-app-bar>
    <GmapMap
      ref="mapRef"
      :center="department.location"
      style="width:100%"
      :style="mapSize"
      :options="mapOptions"
      :map-type-id="mapType"
    >
      <MapContent
        ref="mapContentRef"
        :base="department.location"
        :department="department"
        :courses="courses"
        :drivers="drivers"
        :map-ref="$refs.mapRef"
        :sidebar="!!sidebar.size"
        @windowOpen="onMarkerWindowOpen"
      />
    </GmapMap>
    <div class="table-statistics" ref="statisticsBar">
      <div class="table-statistics__value mr-2">
        {{ countCourses[0] }}
      </div>
      <div class="table-statistics__label">
        {{ countCourses[1] }}
      </div>
      <TableStatisticsBar :table-name="tableName" drivers-occupancy />
    </div>

    <Sidebar ref="preview">
      <SinglePreviewRoute />
    </Sidebar>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { navigateTableShortkeys } from '../../const/shortKeys'
import { mapDirectionsParameters } from '../../helpers/mapOptions'
import Sidebar from '../../components/Layout/Sidebar/Sidebar'
import filtersMixin from '../../mixins/filtersMixin'
import SearchBar from '../../components/Filters/SearchBar'
import StandingTime from '../../components/Filters/StandingTime'
import DateRangePicker from '../../components/Filters/DateRangePicker'
import ShowFiltersButton from '../../components/Filters/ShowFiltersButton'
import MapFilters from '../../components/Map/MapFilters'
import MapContent from '../../components/Map/MapContent'
import MapSwitch from '../../components/Map/MapSwitch'
import SinglePreviewRoute from '../../components/Single/SinglePreviewRoute'
import TableStatisticsBar from '../../components/Table/TableStatisticsBar'
import { readJSON, declensionName } from '../../utils'
import webSocketMixin from '../../mixins/webSocketMixin'
import mapMixin from '../../mixins/mapMixin'

export default {
  components: {
    Sidebar,
    SearchBar,
    StandingTime,
    DateRangePicker,
    ShowFiltersButton,
    MapFilters,
    MapContent,
    MapSwitch,
    TableStatisticsBar,
    SinglePreviewRoute
  },
  mixins: [filtersMixin, webSocketMixin, mapMixin],
  data: () => ({
    tableName: 'coursesMap',
    navigateTableShortkeys,
    displayMode: 'all', // can be 'drivers', 'courses' or 'all' - filter defined in MapFilters due to no connection with BE
    directionsRendererInstance: null,
    spreadCourses: [],
    spreadCoursesSignature: '',
    // While a marker window is open, keep a stable drivers reference so DriverPositionsChannel
    // updates do not re-render MapContent (which remounts MarkerWrapper and blinks the window).
    pinnedDrivers: null
  }),
  channels: {
    MapChannel: {
      received(data) {
        this.captureChanges(data, 'coursesMap', true)
      }
    },
    DriverPositionsChannel: {
      received(data) {
        this.captureChanges(data, 'driversLocation', true)
      }
    }
  },
  computed: {
    ...mapState({
      coursesMap: state => state.tables.coursesMap,
      driversLocation: state => state.tables.driversLocation,
      sidebar: state => state.layout.sidebar,
      directions: state => state.route.entity.directions,
      showFilters: state => state.coursesMap.showFilters,
      mapCourses: state => state.coursesMap.items,
      mapType: state => state.core.mapType,
      liveDrivers: state => state.driversLocation.items
    }),
    drivers() {
      if (this.displayMode === 'courses') return []
      // Prefer pinned snapshot while window is open — avoids tracking liveDrivers in this branch
      if (this.pinnedDrivers) return this.pinnedDrivers
      return this.liveDrivers
    },
    mapDateRange() {
      return this.$store.state[this.tableName].dateRange
    },
    courses() {
      if (this.displayMode === 'drivers') return []
      return this.spreadCourses
    },
    countCourses() {
      const itemsCount = this.courses.length
      return declensionName(itemsCount, 'kurs', 'kursy', 'kursów').split(' ')
    },
    allFilters() {
      return this.coursesMap.filters
        .map(item => {
          item.tableName = 'coursesMap'
          return item
        })
        .concat(
          this.driversLocation.filters.map(item => {
            item.tableName = 'driversLocation'
            return item
          })
        )
    },
    mapSize() {
      const sidebarWidth = this.$refs.preview?.$el.offsetWidth || 0
      return {
        height: this.mapHeight + 'px',
        width: 'calc(100% - ' + (this.sidebar.size > 0 ? sidebarWidth : 0) + 'px)'
      }
    }
  },
  watch: {
    directions(directions) {
      if (!this.$refs.mapRef) return
      this.$refs.mapRef.$mapPromise.then(() => {
        this.updateDirectionsRenderer(directions)
      })
    },
    mapCourses: {
      handler(value) {
        this.refreshSpreadCourses(value)
      },
      immediate: true
    },
    mapDateRange(newDate) {
      this.resetRoutePlanning()
      const [channel] = this.activeChannels
      this.unsubscribeSocket(channel)
      this.subscribeSocket(channel, { dueDate: newDate[0] })
    }
  },
  created() {
    this.setTableConfig({ tableName: 'driversLocation' })
  },
  mounted() {
    this.subscribeSocket('MapChannel', { dueDate: this.mapDateRange[0] })
    this.subscribeSocket('DriverPositionsChannel')
  },
  beforeDestroy() {
    this.disposeDirectionsRenderer()
    this.closeSidebar()
    this.unsubscribeSocket('MapChannel')
    this.unsubscribeSocket('DriverPositionsChannel')
  },
  methods: {
    ...mapActions({
      setSidebar: 'layout/setSidebar',
      closeSidebar: 'layout/closeSidebar',
      resetRoutePlanning: 'route/resetRoutePlanning'
    }),
    ensureDirectionsRenderer() {
      if (this.directionsRendererInstance || !this.google?.maps || !this.$refs.mapRef?.$mapObject) {
        return this.directionsRendererInstance
      }
      this.directionsRendererInstance = new this.google.maps.DirectionsRenderer(mapDirectionsParameters)
      this.directionsRendererInstance.setMap(this.$refs.mapRef.$mapObject)
      return this.directionsRendererInstance
    },
    updateDirectionsRenderer(directions) {
      const renderer = this.ensureDirectionsRenderer()
      renderer?.setDirections(directions)
    },
    disposeDirectionsRenderer() {
      if (!this.directionsRendererInstance) return
      this.directionsRendererInstance.setMap(null)
      this.directionsRendererInstance = null
    },
    buildCoursesSpreadSignature(items) {
      if (!items?.length) return '0'
      return items
        .map(item =>
          [
            item.id,
            item.locationLat,
            item.locationLng,
            item.courseStatus,
            item.courseType,
            item.containerIdleDays,
            item.orderId
          ].join(':')
        )
        .join('|')
    },
    // spreadOverlappingMarkers mutates items (spreadLocation), so we must clone via readJSON.
    // That clone + radial layout is expensive on hundreds of courses, so we only run it when
    // map-relevant fields change (signature), not on every unrelated parent re-render.
    refreshSpreadCourses(items = this.mapCourses) {
      const signature = this.buildCoursesSpreadSignature(items)
      if (signature === this.spreadCoursesSignature) return

      const shouldFitBounds = items.length > 0 && items.every(val => !val.fromWS)
      this.spreadCoursesSignature = signature
      this.spreadCourses = items.length ? this.spreadOverlappingMarkers(readJSON(items), 'coursesMap') : []

      if (shouldFitBounds) {
        this.$nextTick(() => this.fitBounds(this.spreadCourses))
      }
    },
    handleShortKey(event) {
      if (event.srcKey === 'esc') this.closeSidebar()
    },
    onMarkerWindowOpen(isOpen) {
      // Snapshot current drivers so the computed stops tracking live store updates
      // to prevent opened windows from blinking when drivers are updated
      this.pinnedDrivers = isOpen ? this.drivers : null
    }
  }
}
</script>
